import React, { useEffect, useState, useCallback } from 'react';
import { 
    Box, Grid, Card, CardContent, Typography, Button, 
    CircularProgress, Alert, Snackbar, Chip, Pagination
} from '@mui/material';
import { AmslerGridData, getEyeTestedDisplay, RIGHT_EYE_TYPE } from '../types/AmslerGridData';
import { styled } from '@mui/system';
import AmslerGridDialog from './AmslerGridDialog';
import AmslerGridPlaceholder from './AmslerGridPlaceholder';
import { useTheme } from '@mui/material/styles';
import { useFirebaseService } from '../contexts/FirebaseServiceContext';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'scale(1.02)',
        cursor: 'pointer',
    },
}));

const ImageContainer = styled(Box)({
    position: 'relative',
    paddingTop: '100%', // Square aspect ratio
    backgroundColor: '#f5f5f5',
    width: '100%',
});

const EyeBadge = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isRight',
})<{ isRight: boolean }>(({ isRight, theme }) => ({
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: isRight ? '#bbdefb' : '#f8bbd0',
    color: isRight ? '#1565c0' : '#c2185b',
    padding: '4px 8px',
    borderRadius: 16,
    fontSize: '0.75rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
}));

interface GridItemProps {
    grid: AmslerGridData;
    onOpenImage: (grid: AmslerGridData, imageUrl?: string) => void;
    onDelete?: (grid: AmslerGridData) => void;
}

const GridItem: React.FC<GridItemProps> = ({ grid, onOpenImage, onDelete }) => {
    const theme = useTheme();
    const isRightEye = grid.eyeTested === RIGHT_EYE_TYPE;
    const formattedDate = new Date(grid.timestamp).toLocaleDateString();
    const eyeTypeText = getEyeTestedDisplay(grid.eyeTested);
    
    // Generate image on the fly when needed
    const { firebaseService } = useFirebaseService();
    const handleClick = () => {
        const generatedImageUrl = firebaseService.generateGridImage(grid);
        onOpenImage(grid, generatedImageUrl);
    };

    return (
        <StyledCard
            onClick={handleClick}
            sx={{
                border: `2px solid ${isRightEye ? theme.palette.primary.main : theme.palette.secondary.main}`,
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" component="div">
                        {formattedDate}
                    </Typography>
                    <Chip
                        label={eyeTypeText}
                        color={isRightEye ? 'primary' : 'secondary'}
                        size="small"
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <AmslerGridPlaceholder
                        eyeType={grid.eyeTested}
                        withMarkedDots={grid.dots || []}
                    />
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Anomalies: {grid.dots?.length || 0}
                    </Typography>
                    {grid.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Notes: {grid.notes}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </StyledCard>
    );
};

const Dashboard: React.FC = () => {
    const [grids, setGrids] = useState<AmslerGridData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedGrid, setSelectedGrid] = useState<AmslerGridData | null>(null);
    const [preloadedImageUrl, setPreloadedImageUrl] = useState<string | undefined>(undefined);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const [page, setPage] = useState(0);
    const [totalGrids, setTotalGrids] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const LIMIT = 10; // Number of items per page

    const { firebaseService } = useFirebaseService();

    useEffect(() => {
        const loadGrids = async () => {
            try {
                setLoading(true);
                const userGrids = await firebaseService.getUserGrids({
                    page,
                    limit: LIMIT
                });
                
                // Log grid data for debugging
                console.log('Loaded grids:', userGrids);
                
                // Extract the grids array from the response object
                setGrids(userGrids.grids);
                setTotalGrids(userGrids.total);
                setHasMore(userGrids.hasMore);
                setError(null);
            } catch (err) {
                console.error('Error loading grids:', err);
                setError('Failed to load grids');
                setSnackbar({
                    open: true,
                    message: 'Failed to load grids',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        loadGrids();
    }, [firebaseService, page, LIMIT]);

    // Calculate total pages
    const totalPages = Math.ceil(totalGrids / LIMIT);

    // Handle page change
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value - 1); // MUI Pagination is 1-indexed, but our API is 0-indexed
    };

    const handleOpenImage = useCallback((grid: AmslerGridData, imageUrl?: string) => {
        console.log('Opening grid:', grid.id, 'with preloaded URL:', imageUrl ? 'available' : 'not available');
        setSelectedGrid(grid);
        setPreloadedImageUrl(imageUrl);
        setDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
        setSelectedGrid(null);
        setPreloadedImageUrl(undefined);
    }, []);

    const handleShare = useCallback((grid: AmslerGridData) => {
        // Implement sharing functionality
        console.log('Sharing grid:', grid);
    }, []);

        return (
            <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Amsler Grid Tests
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : grids.length === 0 ? (
                <Alert severity="info">
                    No Amsler Grid tests found. Start by taking a new test.
                </Alert>
            ) : (
                <>
                <Grid container spacing={3}>
                {grids.map((grid) => (
                        <Grid item xs={12} sm={6} md={4} key={grid.id}>
                            <GridItem
                                grid={grid}
                                onOpenImage={handleOpenImage}
                            />
                    </Grid>
                ))}
            </Grid>
                    
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination 
                                count={totalPages} 
                                page={page + 1} 
                                onChange={handlePageChange}
                                color="primary"
                                disabled={loading}
                            />
                        </Box>
                    )}
                </>
            )}

            <AmslerGridDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                grid={selectedGrid}
                onShare={handleShare}
                onDownload={(grid: AmslerGridData) => {
                    // Implementation for download
                    if (grid) {
                        const imageUrl = firebaseService.generateGridImage(grid);
                        const a = document.createElement('a');
                        a.href = imageUrl;
                        a.download = `amsler-grid-${new Date(grid.timestamp).toISOString().split('T')[0]}.png`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                    }
                }}
            />
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Dashboard; 